import {
  type VDEstimatedEvent,
  type VideoLinkItem,
} from '@solink/vd-data-layer/api-clients/platform/platform';
import {
  type CallhomeSite,
  type CHCameraId,
  type SiteId,
  type VMSCameraId,
} from '@solinkcorp/api-types';
import {
  type EventStandardDefs,
  type Field,
  type FieldI18n,
  type LanguagesType,
} from '@solinkcorp/event-standard';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { type EstimatedEvent } from '@solinkcorp/motion-events-estimator';
import {
  EVENT_FIELD_STRING_TYPE_KEYWORD,
  EVENT_FIELD_STRING_TYPE_TEXT,
  EVENT_SUBTYPE_ALARM,
  EVENT_SUBTYPE_DOWNLOAD,
  EVENT_TYPE_BOOKMARK,
  EVENT_TYPE_MOTION,
  EVENT_TYPE_TRANSACTION,
  EventUtils,
} from '@solinkcorp/vd-common';
import { type EventCamera, type IEvent } from 'clients/PlatformApi';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import orderBy from 'lodash/orderBy';
import sortedIndexBy from 'lodash/sortedIndexBy';
import startsWith from 'lodash/startsWith';
import moment from 'moment';
import IntervalTree from 'node-interval-tree';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { IS_PRODUCTION } from 'src/environment';
import { LocationStore } from 'stores/LocationStore';
import PreferenceStore from 'stores/PreferenceStore';
import { getReduxStore } from 'stores/store';
import * as LocaleUtil from 'utils/LocaleUtil';
import {
  CLOUD_STATUS_COMPLETE,
  CLOUD_STATUS_COMPLETE_WITH_ERRORS,
  CLOUD_STATUS_IN_PROGRESS,
  CLOUD_STATUS_VERIFY,
  CONNECT_DOWNLOAD_STATUS_EXPIRED,
  DOWNLOAD_EXPIRATION_TIME,
  EVENT_SUBTYPE_SCHEDULED,
  MINIMUM_PINNED_PLAY_TIME_MS,
  type PushToCloudStatus,
  SCHEDULED_AI_CHAT_REASON,
  SHARE_STATUS_DISABLED,
  VISIBILITY_PRIVATE,
  VISIBILITY_PUBLIC,
  VISIBILITY_SUPPORT,
} from 'vd-constants';
import * as DateHandler from './DateHandler';
import FeaturesUtil from './FeaturesUtil';
import { type CamerasDictionary } from './types';

const isPushToCloudStatusEqual = function isPushToCloudStatusEqual(
  event: IEvent,
  status: PushToCloudStatus,
): boolean {
  return event.pushToCloudStatus === status;
};

/**
 * Check if the provided event is a bookmark.
 */
export function isBookmark(event: IEvent): boolean {
  return isObject(event) && event.type === EVENT_TYPE_BOOKMARK;
}

/**
 * Check if the provided event is a transaction event.
 */
export function isTransaction(event: IEvent): boolean {
  return isObject(event) && event.type === EVENT_TYPE_TRANSACTION;
}

/**
 * POS transaction statuses the UI branches on, as they appear on
 * `details.Status` / the `transaction_status` column (normalized to upper-case).
 * The call-home parsers emit these via their `TransactionStatus` enum and
 * explorer-server keys on the same values — normalize once so every call site
 * classifies identically.
 */
export const TRANSACTION_STATUS = {
  POST_VOID: 'POST VOID',
  SALE: 'SALE',
  SALES: 'SALES',
} as const;

/** Normalize a raw POS status/type string for comparison (trim + upper-case). */
export function normalizeTransactionStatus(value?: string | null): string {
  return typeof value === 'string' ? value.trim().toUpperCase() : '';
}

/**
 * Optimistically detect a post-void transaction from the event alone (no BFF
 * round-trip). POS events carry the transaction status in `details.Status`,
 * which the call-home parsers set to the `TransactionStatus` enum value
 * `'POST VOID'` for a post-void (the same status explorer-server keys on as
 * `TRANSACTION_STATUS = 'post void'`). Consumed by the NDE event video panel to
 * show the "Related transaction" action immediately (grayed until the related
 * transaction loads) rather than waiting on a resolvability probe.
 */
export function isPostVoid(event: IEvent): boolean {
  if (!isObject(event)) return false;
  const status = (event.details as { Status?: unknown } | undefined)?.Status;
  return normalizeTransactionStatus(status as string) === TRANSACTION_STATUS.POST_VOID;
}

/**
 * A plain sale has no original transaction to resolve back to — used to exclude
 * it from the "Related transaction" action even if something unexpectedly
 * resolves. Accepts the raw POS status/type string (e.g. the `transaction_status`
 * column or `details.Status`).
 */
export function isPlainSale(transactionType?: string): boolean {
  const t = normalizeTransactionStatus(transactionType);
  return t === TRANSACTION_STATUS.SALE || t === TRANSACTION_STATUS.SALES;
}

/** Check if the provided event is a motion event.
 */
export function isMotion(event: IEvent): boolean {
  return isObject(event) && event.type === EVENT_TYPE_MOTION;
}

/**
 * Check if the provided event can be shared.
 *
 * NOTE: An event that has already been shared, is not considered as shareable.
 */
export function isShareable(event: IEvent): boolean {
  return (
    !isShared(event) &&
    (isTransaction(event) || isMotion(event) || (isBookmark(event) && !isDownloadBookmark(event)))
  );
}

/**
 * Check if the provided event is a transaction event that has been shared.
 */
export function isSharedTransaction(event: IEvent): boolean {
  return isTransaction(event) && isShared(event);
}

/**
 * Check if the provided event is a motion event that has been shared.
 */
export function isSharedMotion(event: IEvent): boolean {
  return isMotion(event) && isShared(event);
}

/**
 * Check if the provided event is a bookmark that has been shared.
 */
export function isSharedBookmark(event: IEvent): boolean {
  return isBookmark(event) && isShared(event);
}

/**
 * Check if the provided event is a download bookmark.
 */
export function isDownloadBookmark(event: IEvent): boolean {
  return isBookmark(event) && event.subtype === EVENT_SUBTYPE_DOWNLOAD;
}

/**
 * Check if the provided event has the download subtype.
 */
export function isDownload(event: IEvent): boolean {
  return event.subtype === EVENT_SUBTYPE_DOWNLOAD;
}

/**
 * Check if the provided event is a standard bookmark.
 */
export function isStandardBookmark(event: IEvent): boolean {
  return (
    isBookmark(event) &&
    (isEmpty(event.subtype) ||
      event.subtype === 'conversion' ||
      event.subtype === EVENT_SUBTYPE_ALARM) &&
    !isShared(event)
  );
}

export function isScheduledActivity(event: IEvent): boolean {
  return event.subtype === EVENT_SUBTYPE_SCHEDULED;
}

export function isInFuture(event: IEvent): boolean {
  return moment(event.startTime).isAfter(Date.now());
}

export function isActiveNow(event: IEvent): boolean {
  return moment(event.startTime).isBefore(Date.now()) && moment(event.endTime).isAfter(Date.now());
}

export function isPrivateEvent(event: IEvent) {
  return event.scope?.visibility === VISIBILITY_PRIVATE;
}

export function isPast(event: IEvent): boolean {
  return moment(event.endTime).isSameOrBefore(Date.now());
}

export function shouldThumbnailReady(event: IEvent): boolean {
  return moment(event.startTime).isSameOrBefore(moment().subtract(10, 'minutes'));
}

/**
 * Check whether the event's push-to-cloud status is in-progress.
 */
export function isPushToCloudInProgress(event: IEvent): boolean {
  return isPushToCloudStatusEqual(event, CLOUD_STATUS_IN_PROGRESS);
}

/**
 * Check whether the event's push-to-cloud status is complete.
 */
export function isPushToCloudComplete(event: IEvent): boolean {
  return isPushToCloudStatusEqual(event, CLOUD_STATUS_COMPLETE);
}

/**
 * Check whether the event's push-to-cloud status is verify
 * This is the step before complete. The only difference is the clip hasn't been shared to the email recipients.
 */
export function isPushToCloudVerify(event: IEvent): boolean {
  return isPushToCloudStatusEqual(event, CLOUD_STATUS_VERIFY);
}

/**
 * Check whether the event's push-to-cloud status is complete with errors.
 */
function isPushToCloudCompleteWithErrors(event: IEvent): boolean {
  // TODO: This will never be true?  Unless we set event.pushToCloudStatus to
  // CLOUD_STATUS_COMPLETE_WITH_ERRORS somewhere in video-discovery?
  return isPushToCloudStatusEqual(event, CLOUD_STATUS_COMPLETE_WITH_ERRORS as any);
}

/**
 * Check whether the event should have video segments in the cloud.
 */
export function isEventInCloud(event: IEvent): boolean {
  return (
    isPushToCloudInProgress(event) ||
    isPushToCloudComplete(event) ||
    isPushToCloudCompleteWithErrors(event)
  );
}

/**
 * Check if the provided event has been shared.
 */
export function isShared(event?: { shares?: IEvent['shares'] }): boolean {
  return isObject(event) && !isEmpty(event.shares);
}

/**
 * Check if the event has cameras.
 */
export function hasCameras(event: IEvent): boolean {
  return event.cameras && event.cameras.length > 0;
}

/**
 * True if the event has at least one camera with a thumbnail or frame images to
 * show — i.e. there is video to display. Used to decide whether to render a
 * video thumbnail (vs. a receipt preview) and whether clicking the event should
 * open the video player or the receipt panel.
 */
export function hasVideoThumbnails(event: IEvent): boolean {
  const { cameras } = getCamerasForThumbnails(event);
  const list = cameras.length > 0 ? cameras : (event.cameras ?? []);
  return list.some((camera) => Boolean(camera.thumbnail) || (camera.images?.length ?? 0) > 0);
}

/*
 * Certain cameras ids don't actually correspond to real cameras with video
 * For e.g. Open AI events, we associate a fake camera id with the event
 * This function checks if the camera id is fake or not by checking the camerasDictionary from the location cameraa
 */

export function hasCallhomeCameras(event: IEvent, camerasDictionary: CamerasDictionary): boolean {
  return event.cameras?.some((camera) => camera.chId && camerasDictionary[camera.chId]);
}

/**
 * Retrieve cameras to be used to render the thumbnails of that event.
 * If the event has more than 1 camera attached, return the cameras belonging to one of the following groups:
 * * cameras added via user-defined data source annotation
 * * cameras added via data source annotations determined by AI
 * * cameras added via pos / camera association in callhome
 */
export function getCamerasForThumbnails(event: IEvent): {
  cameras: EventCamera[];
  source: 'user' | 'ai' | 'all';
} {
  const showAllEventThumbnails = PreferenceStore.shouldShowAllEventThumbnails();

  if (showAllEventThumbnails || event.cameras.length === 1 || !event.dataSource) {
    return { cameras: event.cameras, source: 'all' };
  }

  // Temporarily check if camera annotations are arrays. They are bein returned as objects in some cases from the alarm events API from the cache.
  const aiAnnCameras = event.cameras.filter((cam) =>
    cam.annotations?.find((ann) => ann.label === event.dataSource && ann.subtype === 'AI'),
  );
  const userAnnCameras = event.cameras.filter((cam) =>
    cam.annotations?.find((ann) => ann.label === event.dataSource && ann.subtype !== 'AI'),
  );
  const rest = event.cameras.filter(
    (cam) => !cam.annotations?.find((ann) => ann.label === event.dataSource),
  );

  if (userAnnCameras.length > 0) {
    return { cameras: userAnnCameras, source: 'user' };
  } else if (aiAnnCameras.length > 0) {
    return { cameras: aiAnnCameras, source: 'ai' };
  }
  return { cameras: rest, source: 'all' };
}

function isSystemGenerated(email: string): boolean {
  return startsWith(email, 'device+');
}

/**
 * Check if the event exists locally or in the cloud.
 */
export function isInCloud(
  event: IEvent,
  location: CallhomeSite,
  cameraId: string,
  streamId: string,
): boolean {
  let start = 0;

  if (event && event.startTime) {
    // TODO This check should not be necessary after this: https://github.com/SolinkCorp/vd-web-data-layer/pull/10
    if (typeof event.startTime.getTime === 'function') {
      start = event.startTime.getTime();
    } else {
      start = new Date(event.startTime).getTime();
    }
  }

  const camera = find(location.cameras, (o) => {
    return o.cameraId === cameraId;
  });
  if (!camera) {
    return false;
  }

  const stream = find(camera.streams, ['id', streamId]);
  if (!stream) {
    return true;
  }

  let streamEarliestSegmentDate: number | undefined;
  if (typeof stream.earliestSegmentDate === 'string') {
    streamEarliestSegmentDate = parseInt(stream.earliestSegmentDate, 10);
  } else {
    streamEarliestSegmentDate = stream.earliestSegmentDate;
  }

  const hasLocalVideo =
    stream.id === streamId && streamEarliestSegmentDate && streamEarliestSegmentDate < start;
  if (hasLocalVideo) {
    return false;
  }

  if (
    stream.cloudVMS &&
    stream.cloudVMS.earliestSegmentDate &&
    stream.cloudVMS.earliestSegmentDate <= start
  ) {
    return true;
  }

  return false;
}

/**
 * Check if all downloads in the event expired and get expiration info
 */
export function checkDownloadEventExpiration(
  event: IEvent,
  locale?: LocaleDefinition,
): {
  hasExpired: boolean;
  downloadExpiry: string;
} {
  let hasExpired = false;
  let downloadExpiry = '';

  // TODO: IEvent has no property named `downloads`.  Where does this come from?
  const downloads = (event as any).downloads;
  const { startTime } = event;

  if (!isEmpty(downloads)) {
    const { [0]: lastExpireDownload } = orderBy(downloads, ['expiration'], ['desc']);
    const { expiration, state } = lastExpireDownload;

    if (!isNil(expiration)) {
      hasExpired =
        expiration <= DateHandler.getTimestamp() || state === CONNECT_DOWNLOAD_STATUS_EXPIRED;

      if (locale) {
        downloadExpiry = hasExpired
          ? locale.download.itemExpired
          : `${locale.download.expires}: ${moment(expiration).format('ll')}`;
      }
    } else {
      // hack for inconsistent data (some events doesn't have expiration)
      hasExpired = +new Date(startTime) + DOWNLOAD_EXPIRATION_TIME <= DateHandler.getTimestamp();

      if (locale) {
        downloadExpiry = hasExpired ? locale.download.itemExpired : '';
      }
    }
  }

  return { hasExpired, downloadExpiry };
}

function getClipCreatorEmail(shares: NonNullable<IEvent['shares']>) {
  // look for the share that has the same email as the sharedBy field
  // it's the entry of the clip creator
  const shareEntryOfCreator = shares.find((share) => share.email === share.sharedBy);
  if (!shareEntryOfCreator) {
    console.error('No share entry found for clip creator');
  }
  return shareEntryOfCreator?.sharedBy || shares[0]?.sharedBy || '';
}

/**
 * Utility function to generate the "Shared by..." and "Last updated by..." strings.
 *
 * @param options.event
 * @param options.lastUpdatedBy - email address of user
 * @param options.lastUpdateTime - ISO Time String - e.g. 2019-03-27T18:56:51.152+00:00
 * @param options.locale
 * @return Object with the two strings desired.
 */
export function getCreatedByUpdatedByStrings(options: {
  event: IEvent;
  lastUpdatedBy: string | undefined;
  lastUpdateTime: string | undefined;
  locale: LocaleDefinition;
}): { createdBy: string; updatedBy: string } {
  const { event, lastUpdatedBy, lastUpdateTime, locale } = options;
  const bookmark = isBookmark(event);

  let createdBy = '';
  let updatedBy = '';

  if (!bookmark && isShared(event)) {
    // Event that has been shared (bookmark or other)
    forEach(event.shares, (share) => {
      if (share.sharedBy === share.email) {
        const { time: shareTime, date: shareDate } = DateHandler.toCompactString(
          share.sharedAt as number,
        );
        createdBy = isEmpty(shareTime)
          ? LocaleUtil.getLocalizedStr({
              string: locale.event.sharedByDate,
              replacements: {
                email: share.sharedBy,
                date: shareDate,
              },
            })
          : LocaleUtil.getLocalizedStr({
              string: locale.event.sharedByTime,
              replacements: {
                email: share.sharedBy,
                time: shareTime,
              },
            });
        return false;
      }
    });
  } else if (bookmark) {
    const createTime = event.createTime;

    let creatorEmail = event.details?.creator;
    if (!creatorEmail && event.shares) {
      creatorEmail = getClipCreatorEmail(event.shares);
    }

    if (creatorEmail && createTime && !isSystemGenerated(creatorEmail)) {
      const { time: createTimeStr, date: createDateStr } = DateHandler.toCompactString(createTime);

      const replacementsDate = { email: creatorEmail, date: createDateStr };
      const replacementsTime = { email: creatorEmail, time: createTimeStr };

      if (isSharedBookmark(event)) {
        createdBy = isEmpty(createTimeStr)
          ? LocaleUtil.getLocalizedStr({
              string: locale.event.sharedByDate,
              replacements: replacementsDate,
            })
          : LocaleUtil.getLocalizedStr({
              string: locale.event.sharedByTime,
              replacements: replacementsTime,
            });
      } else if (isDownloadBookmark(event)) {
        createdBy = isEmpty(createTimeStr)
          ? LocaleUtil.getLocalizedStr({
              string: locale.event.downloadedByDate,
              replacements: replacementsDate,
            })
          : LocaleUtil.getLocalizedStr({
              string: locale.event.downloadedByTime,
              replacements: replacementsTime,
            });
      } else {
        createdBy = isEmpty(createTimeStr)
          ? LocaleUtil.getLocalizedStr({
              string: locale.event.createdByDate,
              replacements: replacementsDate,
            })
          : LocaleUtil.getLocalizedStr({
              string: locale.event.createdByTime,
              replacements: replacementsTime,
            });
      }
    }

    if (lastUpdatedBy && lastUpdateTime && !isSystemGenerated(lastUpdatedBy)) {
      const { time: updateTime, date: updateDate } = DateHandler.toCompactString(lastUpdateTime);
      updatedBy = isEmpty(updateTime)
        ? LocaleUtil.getLocalizedStr({
            string: locale.event.updatedByDate,
            replacements: {
              email: lastUpdatedBy,
              date: updateDate,
            },
          })
        : LocaleUtil.getLocalizedStr({
            string: locale.event.updatedByTime,
            replacements: {
              email: lastUpdatedBy,
              time: updateTime,
            },
          });
    }
  }

  return { createdBy, updatedBy };
}

/**
 * Query event-standard for existing i18n field data.
 * @param fieldId id of the field in event standard
 * @returns a {fullName, shortName} object if the data exist for this field, undefined otherwise
 */
export function i18nEventFieldData(
  fieldId: string,
  eventStandard: EventStandardDefs,
  localeCode?: string,
): FieldI18n['en'] | undefined {
  const field: Field | undefined = eventStandard[fieldId];
  const i18n = field?.i18n;
  const localeCodeToUse = localeCode ?? getReduxStore().getState().localeState.localeCode;

  if (i18n) {
    return (
      i18n[localeCode as LanguagesType] ||
      i18n[localeCodeToUse.split('-')[0] as LanguagesType] ||
      i18n.en
    );
  }
  return undefined;
}

/**
 * Return the localized event field name, according to the current locale and the data defined in event standard
 * @param fieldId id of the field in event standard, may contain separating . characters
 * @param useFullName set True to return the full name of the field. Default returns the short name.
 */
export function i18nEventFieldName(
  fieldId: string,
  useFullName: boolean,
  eventStandard: EventStandardDefs,
  localeCode?: string,
): string {
  const fieldI18n = i18nEventFieldData(fieldId, eventStandard, localeCode) || {
    fullName: fieldId,
    shortName: fieldId,
  };
  return (
    (useFullName && fieldI18n.fullName) || fieldI18n.shortName || fieldI18n.fullName || fieldId
  );
}

/* Convert the raw field name to a user friendly name. */
export function getFriendlyFieldName(fieldId: string): string {
  const elements = fieldId.replace('.keyword', '').split(/\./);
  const rawName = elements[elements.length - 1];
  // the raw name of the field can be in camel case, like 'favouritedBy', or already user friendly like 'HME ID'
  return /\s/.test(rawName)
    ? rawName
    : rawName.replace(/(?<=\w)([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase());
}

/**
 * Return the localized name of the category that a event field belongs to, according to the current locale and the data defined in the event standard
 * @param fieldId id of the field in event standard, may contain separating . characters
 * @param localeCode
 */
export function i18nEventFieldCategoryName(
  fieldId: string,
  categoryId: string,
  eventStandard: EventStandardDefs,
  localeCode?: string,
): string {
  const valuesI18n = i18nEventFieldData(fieldId, eventStandard, localeCode);
  return valuesI18n?.categoryNames?.[categoryId] || categoryId;
}

/**
 * Return a mapping of field values to their localized printable counterpart according to the current locale
 * and the data defined in event standard
 *
 * @param fieldId id of the field in event standard, may contain separating . characters
 */
export function i18nEventFieldEnumValues(
  fieldId: string,
  eventStandard: EventStandardDefs,
  localeCode?: string,
): { [key: string]: string } | undefined {
  const field = eventStandard[fieldId];
  const valuesI18n = i18nEventFieldData(fieldId, eventStandard, localeCode) || field;
  return valuesI18n?.values;
}

export function isEventFieldDisabled(eventStandard: EventStandardDefs, fieldId: string) {
  const field: Field | undefined = eventStandard[fieldId];
  return !!field?.disabled;
}

export function getEventFieldKeywordType({
  eventStandard,
  fieldId,
  fieldStatsDict,
}: {
  eventStandard: EventStandardDefs;
  fieldId: string;
  fieldStatsDict: { [fieldId: string]: { name: string } };
}) {
  return EventUtils.isEventFieldKeywordType({ eventStandard, fieldId, fieldStatsDict })
    ? EVENT_FIELD_STRING_TYPE_KEYWORD
    : EVENT_FIELD_STRING_TYPE_TEXT;
}

export function isAlarm(event: IEvent | EstimatedEvent | undefined): boolean {
  return (event && 'subtype' in event && event.subtype === EVENT_SUBTYPE_ALARM) || false;
}

export function isAccessControl(event: IEvent | EstimatedEvent | undefined): boolean {
  if (!event) return false;

  if ('subtype' in event && event.subtype === 'ACCESS') {
    return true;
  }

  return (
    'videoLinkItems' in event &&
    !!event.videoLinkItems?.some(
      (item: VideoLinkItem) => item?.title && item.title.includes('Entry Request'),
    )
  );
}

export function isOpen(event: IEvent | undefined): boolean {
  return event?.state === 'OPEN';
}

export function isClosed(event: IEvent | undefined): boolean {
  return event?.state === 'CLOSED';
}

/**
 * @returns true if event matches the playerId.
 */
// TODO: jwalton - Write unit tests for this.
export function eventMatchesCameraId(
  event: IEvent | VDEstimatedEvent,
  playerId: { locationId: SiteId; vmsCameraId: VMSCameraId } | undefined,
) {
  return (
    playerId &&
    event.siteId === playerId.locationId &&
    (event.primaryCameraId === playerId.vmsCameraId ||
      ('cameras' in event && event.cameras.some((camera) => camera.id === playerId.vmsCameraId)))
  );
}

export function isDuringEvent(
  event: { startTime: Date; endTime: Date } | undefined,
  time: number | Date,
) {
  return (
    event &&
    event.startTime.valueOf() <= time.valueOf() &&
    event.endTime.valueOf() >= time.valueOf()
  );
}

/**
 * This verifies that the passed in sorted list of events is sorted in
 * the correct direction (ascending or descending).
 *
 * Note that this generally only compares the first two elements in the list.
 * This is meant as a runtime sanity check - it doesn't check that all elements
 * are actually in sorted order.
 */
function assertSortOrder(events: readonly { startTime: Date }[], reversed: boolean) {
  if (events.length < 2) {
    // All lists with 1 or 0 elements are sorted.
    return;
  }

  const firstDate = events[0].startTime;

  // Find a date that isn't the same as the first date.
  let i = 1;
  while (i < events.length && events[i].startTime.valueOf() === firstDate.valueOf()) {
    i++;
  }

  if (!events[i]) {
    // If there are no events that don't have the same date as the first date,
    // then we are sorted!
    return;
  }

  const forwardSorted = events[i].startTime.valueOf() > firstDate.valueOf();
  if (forwardSorted !== !reversed) {
    throw new Error(`Expected events to be in ${reversed ? 'reverse ' : ''}sorted order`);
  }
}

/**
 * Find the index of the event with the highest startTime before the specified
 * time.
 *
 * If there is one or more events in progress at the specified time, this will
 * return one of them.  If the specified time is between two events, this will
 * return the index of the event before the specified time, even though it is
 * not in progress.  If there are no events that start before the specified time,
 * returns -1.
 *
 * Note that if `options.reversed` is passed in, this will still return -1 of
 * there are no events that start before the specified time, and will return 0
 * if all events start before the specified time.
 *
 * @param events - List of events in ascending sorted order by startTime.
 * @param time - The time to search for.
 * @param [options.reversed] - True if events are in reverse order.
 */
export function findClosestEventIndex(
  events: readonly { startTime: Date; endTime: Date }[],
  time: Date | number,
  options: {
    reversed: boolean;
  } = { reversed: false },
): number {
  if (events.length === 0) {
    return -1;
  }

  if (options.reversed) {
    return findClosestEventIndexReversed(events, time);
  }

  if (!IS_PRODUCTION) {
    // Extra sanity checks for development.
    assertSortOrder(events, false);
  }

  let index = sortedIndexBy<{ startTime: number | Date }>(
    events,
    { startTime: time.valueOf() },
    (item) => item.startTime.valueOf(),
  );

  // Because we're looking for the insertion index, we'll be off-by-one unless
  // the startTime matches exactly.
  if (events[index] && events[index].startTime.valueOf() > time.valueOf()) {
    index--;
  } else if (index >= events.length && events[index - 1]) {
    index = events.length - 1;
  }

  return index;
}

function findClosestEventIndexReversed(
  events: readonly { startTime: Date; endTime: Date }[],
  time: Date | number,
): number {
  if (events.length === 0) {
    return -1;
  }

  if (!IS_PRODUCTION) {
    // Extra sanity checks for development.
    assertSortOrder(events, true);
  }

  let index = sortedIndexBy<{ startTime: number | Date }>(
    events,
    { startTime: time.valueOf() },
    (item) => -item.startTime.valueOf(),
  );

  // Because we're looking for the insertion index, we'll be off-by-one unless
  // the startTime matches exactly.
  if (events[index] && events[index].startTime.valueOf() > time.valueOf()) {
    index--;
  } else if (index === -1 && events[0].startTime.valueOf() <= time.valueOf()) {
    index = 0;
  } else if (index >= events.length) {
    index = -1;
  }

  return index;
}

function getTransactionTitle(event: IEvent): string {
  const { title: eventTitle, type, subtype } = event;

  if (type !== 'transaction' || !eventTitle) {
    return '';
  }

  if (subtype?.match('3PD-')) {
    return eventTitle;
  }

  const test = eventTitle.match(/(.*): (.*)/);

  if (!test) {
    return eventTitle;
  }
  return test[2];
}

/**
 * This is equivalent to `events.indexOf()` but is optimized for the case where
 * events are in sorted order.
 *
 * @param events - The events to search, sorted by startTime in either ascending
 *   or descending order.
 * @param event - The event to search for.
 *
 * @returns -1 if the event could not be found, the index of the event otherwise.
 */
export function findEventIndex(
  events: readonly { startTime: Date; id: string }[],
  event: { startTime: Date; id: string },
  options: {
    reversed: boolean;
  } = { reversed: false },
): number {
  const { reversed } = options;

  if (!IS_PRODUCTION) {
    // Extra sanity checks for development.
    assertSortOrder(events, reversed);
  }

  const factor = reversed ? -1 : 1;
  let index = sortedIndexBy<{ startTime: number | Date }>(
    events,
    event,
    (item) => item.startTime.valueOf() * factor,
  );

  if (index === -1) {
    return index;
  }

  if (events[index]?.id === event.id) {
    return index;
  }

  const searchTime = event.startTime.valueOf();

  // Could be multiple events with the same start time.
  // Find the first event with the given startTime.
  const increment = reversed ? -1 : 1;
  while (
    events[index - increment] &&
    events[index - increment].startTime.valueOf() === searchTime
  ) {
    index -= increment;
  }

  // Search forward for the event.
  while (
    events[index] &&
    events[index].id !== event.id &&
    events[index].startTime.valueOf() === searchTime
  ) {
    index += increment;
  }

  if (events[index] && events[index].id === event.id) {
    return index;
  }

  return -1;
}

/**
 * Returns an event from the collection which overlaps the specified time, or
 * undefined if there is no such event.
 *
 * This only works for estimated events, because for non-estimated events
 * there may be overlap between events, which will never be the case for
 * estimated events.
 */
export function findCurrentEstimatedEvent(
  events: readonly VDEstimatedEvent[],
  time: Date | number,
): VDEstimatedEvent | undefined {
  const index = findClosestEventIndex(events, time);

  if (isDuringEvent(events[index], time)) {
    return events[index];
  }

  return undefined;
}

/** Find which events are "in-progress" for the set of motion estimated events. */
export function getEstimatedEventsForTime(
  motionEstimatedEvents: readonly VDEstimatedEvent[],
  time: number,
) {
  // Motion estimated events never overlap, so we just need to find the event
  // that's currently in-progress.
  const current = findCurrentEstimatedEvent(motionEstimatedEvents, time);
  return current ? [current] : [];
}

/**
 * Constructs an interval tree for a set of nodes, allowing efficient lookup of
 * which events are currently in progress.
 */
export function buildIntervalTree(events: readonly IEvent[]) {
  const intervalTree = new IntervalTree<IEvent>();
  for (const event of events) {
    const start = event.startTime.valueOf();
    let end = event.endTime.valueOf();

    if (end < start) {
      // A surprising number of events in our DB with negative durations, but
      // intervalTree really doesn't like this.
      end = start;
    }

    intervalTree.insert(start, end, event);
  }
  return intervalTree;
}

export function isIEvent(event: IEvent | VDEstimatedEvent): event is IEvent {
  return event.type !== 'estimated';
}

/**
 * Given a set of in-progress events, works out which one is "active".
 *
 * @param playingCameraId - The identifier for the current camera.
 * @param masterTime - The current play time.
 * @param inProgressEventsForCamera - An array of in-progress events
 *   to consider for the active event.  findActiveEvent assumes these
 *   are in sorted order by `startTime`, and also assumes that all
 *   events are for the currently playing camera.
 * @param [options.pinnedEvent] - If there is a pinned event, it will be
 *   given priority over any other events.
 * @param [options.hoveredEvent] - If there is a currently hovered event,
 *   it will be returned if it is for the current camera, and undefined
 *   will be returned otherwise.  Note that `inProgressEventsForCamera`
 *   will be ignored if this is defined.
 */
// TODO: jwalton - Write unit tests for this.
export function findActiveEventForCamera(
  playingCameraId: { locationId: SiteId; vmsCameraId: VMSCameraId } | undefined,
  masterTime: number,
  inProgressEventsForCamera: readonly IEvent[],
  options: {
    pinnedEvent?: IEvent;
    hoveredEvent?: IEvent | VDEstimatedEvent;
  },
): IEvent | undefined {
  const { pinnedEvent, hoveredEvent } = options;

  if (hoveredEvent) {
    if (isIEvent(hoveredEvent) && eventMatchesCameraId(hoveredEvent, playingCameraId)) {
      return hoveredEvent;
    }
    return undefined;
  }

  if (pinnedEvent && inProgressEventsForCamera.includes(pinnedEvent)) {
    return pinnedEvent;
  }

  const index = findClosestEventIndex(inProgressEventsForCamera, masterTime);
  return inProgressEventsForCamera[index];
}

export interface SplitInProgressEvents {
  /** All in-progress events. */
  inProgressEvents: readonly IEvent[];
  /**
   * In-progress events, split up by chCameraId.  This also has the current
   * "active event" for each player - the event we'll be currently showing
   * the receipt for.
   */
  byChCameraId: {
    [chCameraId: string]: readonly IEvent[];
  };
}

/**
 * Given an array of in-progress events, and an array of playing cameras,
 * returns in-progress events for each camera.
 */
export function getSplitInProgressEvents(
  playingCameraIds: { locationId: SiteId; vmsCameraId: VMSCameraId; chCameraId: CHCameraId }[],
  inProgressEvents: readonly IEvent[],
): SplitInProgressEvents {
  const result: SplitInProgressEvents = {
    inProgressEvents,
    byChCameraId: {},
  };

  for (const playingCameraId of playingCameraIds) {
    const inProgressEventsForCamera =
      inProgressEvents?.filter((event) => eventMatchesCameraId(event, playingCameraId)) ?? [];
    result.byChCameraId[playingCameraId.chCameraId] = inProgressEventsForCamera;
  }

  return result;
}

/**
 * Return the "active" event for each camera - this is the event we will show
 * the receipt for overtop of that player.
 */
export function getActiveEventsByChCameraId(
  playingCameraIds: { locationId: SiteId; vmsCameraId: VMSCameraId; chCameraId: CHCameraId }[],
  masterTime: number,
  splitInProgressEvents: SplitInProgressEvents,
  options: {
    pinnedEvent?: IEvent;
    hoveredEvent?: IEvent | VDEstimatedEvent;
  },
): { [playerId: string]: IEvent | undefined } {
  const result: { [playerId: string]: IEvent | undefined } = {};

  for (const playingCameraId of playingCameraIds) {
    const inProgressEventsForCamera =
      splitInProgressEvents.byChCameraId[playingCameraId.chCameraId];
    const activeEvent = inProgressEventsForCamera
      ? findActiveEventForCamera(playingCameraId, masterTime, inProgressEventsForCamera, options)
      : undefined;

    result[playingCameraId.chCameraId] = activeEvent;
  }

  return result;
}
/**
 * Given a set of in-progress events, works out which one is "active".
 *
 * @param masterTime - The current play time.
 * @param inProgressEventsForCamera - An array of in-progress events
 *   to consider for the active event.  findActiveEvent assumes these
 *   are in sorted order by `startTime`, and also assumes that all
 *   events are for the currently playing camera.
 * @param [options.pinnedEvent] - If there is a pinned event, it will be
 *   given priority over any other events.
 * @param [options.hoveredEvent] - If there is a currently hovered event,
 *   it will be returned if it is for the current camera, and undefined
 *   will be returned otherwise.  Note that `inProgressEventsForCamera`
 *   will be ignored if this is defined.
 */
export function findActiveEvent(
  masterTime: number,
  inProgressEvents: readonly IEvent[],
  pinnedEvent: IEvent | undefined,
): IEvent | undefined {
  if (
    pinnedEvent &&
    pinnedEvent.startTime.valueOf() <= masterTime &&
    pinnedEvent.endTime.valueOf() >= masterTime
  ) {
    return pinnedEvent;
  } else if (inProgressEvents.length > 0) {
    const activeEventIndex = findClosestEventIndex(inProgressEvents, masterTime);
    const activeEvent =
      activeEventIndex === -1 || activeEventIndex >= inProgressEvents.length
        ? inProgressEvents[0]
        : inProgressEvents[activeEventIndex];
    return activeEvent;
  }
}

/**
 * Returns true if the given event has no cameras.
 */
export function isDataOnly(event: IEvent | undefined): boolean {
  return event?.cameras === undefined || event.cameras.length === 0;
}

/**
 * Given an array of events, replace the updatedEvent in the array and return
 * the new array.
 */
export function updateEventInArray(
  events: readonly IEvent[],
  updatedEvent: IEvent,
): readonly IEvent[] {
  let result = events;

  const eventIndex = events.findIndex((e) => e.id === updatedEvent.id);
  if (eventIndex > -1) {
    const newEvents = events.slice();
    newEvents[eventIndex] = updatedEvent;
    newEvents.splice(eventIndex, 1, updatedEvent);
    result = newEvents;
  }

  return result;
}

/**
 * Given an array of events, insert the newEvent in the array and return
 * the new array.
 */
export function addEventToArray(events: readonly IEvent[], newEvent: IEvent): readonly IEvent[] {
  let result = events;
  const indexOfFirstEarlierEvent = findIndex(
    events,
    (event) => new Date(newEvent.startTime).getTime() >= new Date(event.startTime).getTime(),
  );
  if (indexOfFirstEarlierEvent > -1) {
    const newEvents = events.slice();
    newEvents.splice(indexOfFirstEarlierEvent, 0, newEvent);
    result = newEvents;
  } else {
    result = events.concat([newEvent]);
  }
  return result;
}

export function removeEventFromArray(
  events: readonly IEvent[],
  targetEvent: IEvent,
): readonly IEvent[] {
  const filteredEvents = events.filter((event) => event.id !== targetEvent.id);
  return filteredEvents;
}

export function getTitle(event: IEvent, locale: LocaleDefinition): string {
  const { title: eventTitle = '', siteId } = event;
  const { downloadExpiry } = checkDownloadEventExpiration(event, locale);
  const location = siteId ? LocationStore.getLocation(siteId) : undefined;
  const locationName = location?.name ?? '...';

  let title = eventTitle;
  if (isTransaction(event)) {
    title = getTransactionTitle(event);
  } else if (isAlarm(event)) {
    title = locationName;
  } else if (isSharedMotion(event)) {
    title = locale.cloudShare.motionTitle;
  } else if (isDownloadBookmark(event) && !isStandardBookmark(event)) {
    title = `${locale.download.defaultTitle} ${downloadExpiry}`;
  }

  return title;
}

export function getDescription(event: IEvent): string {
  return event.details?.description ?? '';
}

export function getFillGaps(event: IEvent): boolean {
  return event.restrictions?.fillGaps ?? false;
}

export function getFaceblur(event: IEvent): boolean {
  return event.restrictions?.faceblur ?? false;
}

export function getShareMessage(event: IEvent): string {
  return event.details?.shareMessage ?? '';
}

export function getSharedClipCreatorId(event: IEvent): string {
  return event.creator ?? '';
}

export function eventIsUserCreated(event: IEvent): boolean {
  return !!event.details?.creator;
}

export function isEventDurationBelowMinimum(event: IEvent) {
  return event.endTime.valueOf() - event.startTime.valueOf() < MINIMUM_PINNED_PLAY_TIME_MS;
}

export function getVisibilityScopeAuditEvents() {
  if (FeaturesUtil.isSolinkUser() || FeaturesUtil.isImpersonating()) {
    return VISIBILITY_SUPPORT;
  }
  return VISIBILITY_PUBLIC;
}

export function isAlarmAndOpen(event: IEvent) {
  return isAlarm(event) && isOpen(event);
}

export function isEventIdInvalid(id: string) {
  // a valid event id can't contain any reserved characters for URLs
  // otherwise we can't get or edit the event data through API `/events/{eventId}`
  const validIdPattern = /^[^!*'"();:@&=+$,/?%#[\]]+$/;
  return !validIdPattern.test(id);
}

export function isScheduledAIChatEvent(event: IEvent) {
  return event?.subtype === SCHEDULED_AI_CHAT_REASON;
}

export function isEventSpotCheckEvent(event: IEvent) {
  // event spot check parameters are stored in details.scheduledActivity.metadata
  // this verifies that it is a spot check (has aiChatAnswers)
  // note: dropped the requirement for subtype to be SPOT_CHECK_AI_CHAT_REASON as it is used to differentiate
  // scheduled spot check from event spot check and we're moving towards unifying the two types to behave the same way as much as possible
  return !!event?.details?.scheduledActivity?.metadata?.aiChatAnswers;
}

export function hasAgentFindings(event: IEvent): boolean {
  return Array.isArray(event?.details?.agentFindings) && event.details.agentFindings.length > 0;
}

export function doesEventHaveMultipleLocationCameras(
  event: IEvent,
  camerasDictionary: CamerasDictionary,
) {
  const uniqueLocationIds = new Set<string>();

  for (const eventCamera of event.cameras) {
    const camera = camerasDictionary[eventCamera.chId ?? ''];

    if (!camera) {
      continue;
    }

    uniqueLocationIds.add(camera.location.id);
  }

  return uniqueLocationIds.size > 1;
}

export function getSelfShare(event: IEvent) {
  return event.shares?.find((share) => {
    const { sharedBy, email } = share;
    return sharedBy === email;
  });
}

export function canViewSavedEvent(event: IEvent) {
  return !isEventIdInvalid(event.id) && isShared(event) && hasCameras(event);
}

export function canViewShareLink(event: IEvent) {
  return (
    isShared(event) && (isPushToCloudComplete(event) || isPushToCloudVerify(event)) && !isKiosk()
  );
}

export function userHasPermissionToArchiveEvent(event: IEvent, isSolinkUser: boolean) {
  const canManageEvents = FeaturesUtil.canManageEvents();
  const hasPublicSharesEnabled = FeaturesUtil.hasPublicSharesEnabled();
  const hasPrivateSharesEnabled = FeaturesUtil.hasPrivateSharesEnabled();
  const eventUserCreated = eventIsUserCreated(event);
  const sharedBookmark = isSharedBookmark(event);
  const downloadBookmark = isDownloadBookmark(event);
  const privateEvent = isPrivateEvent(event);
  const isSharedOrDownloadBookmark = sharedBookmark || downloadBookmark;

  const userHasPermissionToArchive =
    (privateEvent && hasPrivateSharesEnabled && eventUserCreated && isSharedOrDownloadBookmark) ||
    (!privateEvent && hasPublicSharesEnabled && eventUserCreated && isSharedOrDownloadBookmark) ||
    (isSolinkUser && canManageEvents);

  return userHasPermissionToArchive && !isEventIdInvalid(event.id);
}

export function getEventClosedShares(shares: IEvent['shares']) {
  if (!shares) {
    return undefined;
  }

  return shares?.map(({ id }) => {
    return {
      id,
      status: SHARE_STATUS_DISABLED,
    };
  });
}

export function getClipDuration(event: IEvent, locale: LocaleDefinition) {
  if (!event.shareDuration) {
    return undefined;
  }

  const durationInSeconds = event.shareDuration;

  if (durationInSeconds < 60) {
    return `${durationInSeconds} ${locale.general.secondAbbreviation}`;
  } else if (durationInSeconds < 3600) {
    const minutes = Math.round(durationInSeconds / 60);
    return `${minutes} ${locale.general.minuteAbbreviation}`;
  }
  const hours = Math.round(durationInSeconds / 3600);
  return `${hours} ${locale.general.hourAbbreviation}`;
}

export function getClipSize(clipSize: number | undefined = 0, locale: LocaleDefinition) {
  const fileSizeInKB = clipSize / 1000;
  const fileSizeInMB = fileSizeInKB / 1000;
  const fileSizeInGB = fileSizeInMB / 1000;
  const fileSizeInTB = fileSizeInGB / 1000;

  if (fileSizeInTB >= 1) {
    return `${fileSizeInTB.toFixed(1)} ${locale.clipsTable.tb}`;
  } else if (fileSizeInGB >= 1) {
    return `${fileSizeInGB.toFixed(1)} ${locale.clipsTable.gb}`;
  } else if (fileSizeInMB >= 1) {
    return `${fileSizeInMB.toFixed(1)} ${locale.clipsTable.mb}`;
  } else if (fileSizeInKB === 0) {
    return `0 ${locale.clipsTable.kb}`;
  }
  return `${fileSizeInKB.toFixed(1)} ${locale.clipsTable.kb}`;
}
